import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import CustomerLayout from "../components/CustomerLayout";
import DataPagination from "../components/DataPagination";
import api from "../lib/api";
import { 
  MessageSquare, Send, Clock, User,
  Ticket, Plus, ExternalLink, MessageCircle,
  CheckCircle, HelpCircle, XCircle
} from "lucide-react";
import { toast } from "sonner";

const SupportPage = () => {
  const { refreshUser } = useAuth();
  const { t, language } = useLanguage();
  const [tickets, setTickets] = useState([]);
  const [supportInfo, setSupportInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTickets, setTotalTickets] = useState(0);
  
  // New ticket dialog
  const [newTicketOpen, setNewTicketOpen] = useState(false);
  const [ticketSubject, setTicketSubject] = useState("");
  const [ticketMessage, setTicketMessage] = useState("");
  const [ticketCategory, setTicketCategory] = useState("general");
  const [submitting, setSubmitting] = useState(false);
  
  // Ticket detail dialog
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetailOpen, setTicketDetailOpen] = useState(false);
  const [replyMessage, setReplyMessage] = useState("");
  const [sendingReply, setSendingReply] = useState(false);

  // Fetch function for refreshing data
  const fetchTickets = async () => {
    try {
      const [ticketsRes, supportRes] = await Promise.all([
        api.get(`/tickets?page=${currentPage}&limit=10`),
        api.get("/support/info")
      ]);
      setTickets(ticketsRes.data.tickets || []);
      setTotalPages(ticketsRes.data.pages || 1);
      setTotalTickets(ticketsRes.data.total || 0);
      setSupportInfo(supportRes.data);
    } catch (error) {
      console.error("Fetch error:", error);
    }
  };

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  useEffect(() => {
    let isMounted = true;
    
    const fetchData = async () => {
      try {
        const [ticketsRes, supportRes] = await Promise.all([
          api.get(`/tickets?page=${currentPage}&limit=10`),
          api.get("/support/info")
        ]);
        if (isMounted) {
          setTickets(ticketsRes.data.tickets || []);
          setTotalPages(ticketsRes.data.pages || 1);
          setTotalTickets(ticketsRes.data.total || 0);
          setSupportInfo(supportRes.data);
          setLoading(false);
        }
      } catch (error) {
        console.error("Fetch error:", error);
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    fetchData();
    
    return () => {
      isMounted = false;
    };
  }, [currentPage]);

  const handleCreateTicket = async () => {
    if (!ticketSubject.trim() || !ticketMessage.trim()) {
      toast.error(language === "tr" ? "Lütfen tüm alanları doldurun" : "Please fill all fields");
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post("/tickets", {
        subject: ticketSubject,
        message: ticketMessage,
        category: ticketCategory
      });
      toast.success(t("support.ticketSent"));
      setNewTicketOpen(false);
      setTicketSubject("");
      setTicketMessage("");
      setTicketCategory("general");
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error creating ticket");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async () => {
    if (!replyMessage.trim()) return;
    
    setSendingReply(true);
    try {
      await api.post(`/tickets/${selectedTicket.id}/reply`, {
        message: replyMessage
      });
      toast.success(language === "tr" ? "Yanıt gönderildi" : "Reply sent");
      setReplyMessage("");
      
      // Refresh ticket detail
      const res = await api.get(`/tickets/${selectedTicket.id}`);
      setSelectedTicket(res.data);
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error sending reply");
    } finally {
      setSendingReply(false);
    }
  };

  const openTicketDetail = async (ticket) => {
    try {
      const res = await api.get(`/tickets/${ticket.id}`);
      setSelectedTicket(res.data);
      setTicketDetailOpen(true);
    } catch (error) {
      toast.error("Error loading ticket");
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      open: { text: t("support.ticketOpen"), class: "bg-blue-500/20 text-blue-400 border border-blue-500/30", icon: HelpCircle },
      in_progress: { text: t("support.ticketInProgress"), class: "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30", icon: Clock },
      resolved: { text: t("support.ticketResolved"), class: "bg-green-500/20 text-green-400 border border-green-500/30", icon: CheckCircle },
      closed: { text: t("support.ticketClosed"), class: "bg-gray-500/20 text-gray-400 border border-gray-500/30", icon: XCircle }
    };
    const config = statusConfig[status] || statusConfig.open;
    const Icon = config.icon;
    return (
      <span className={`px-2 py-1 text-xs font-mono rounded flex items-center gap-1 ${config.class}`}>
        <Icon className="w-3 h-3" />
        {config.text}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      low: { text: t("support.priorityLow"), class: "bg-gray-500/20 text-gray-400" },
      medium: { text: t("support.priorityMedium"), class: "bg-blue-500/20 text-blue-400" },
      high: { text: t("support.priorityHigh"), class: "bg-orange-500/20 text-orange-400" },
      urgent: { text: t("support.priorityUrgent"), class: "bg-red-500/20 text-red-400" }
    };
    const config = priorityConfig[priority] || priorityConfig.medium;
    return <span className={`px-2 py-0.5 text-xs font-mono rounded ${config.class}`}>{config.text}</span>;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      general: t("support.categoryGeneral"),
      technical: t("support.categoryTechnical"),
      billing: t("support.categoryBilling"),
      other: t("support.categoryOther")
    };
    return labels[category] || category;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <>
      <CustomerLayout activeId="support">
        <div className="p-4 md:p-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-mono font-bold mb-2">{t("support.title")}</h1>
              <p className="text-muted-foreground font-mono text-sm">{t("support.subtitle")}</p>
            </div>
            <Button onClick={() => setNewTicketOpen(true)} className="font-mono">
              <Plus className="w-4 h-4 mr-2" />
              {t("support.newTicket")}
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Tickets List */}
            <div className="lg:col-span-2 space-y-4">
              <h2 className="text-lg font-mono font-bold flex items-center gap-2">
                <Ticket className="w-5 h-5 text-primary" />
                {t("support.myTickets")} ({totalTickets})
              </h2>

              {tickets.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground border border-white/10 rounded-lg">
                  <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="font-mono">{t("support.noTickets")}</p>
                </div>
              ) : (
                <>
                  <div className="space-y-3">
                    {tickets.map((ticket) => (
                      <div
                        key={ticket.id}
                        onClick={() => openTicketDetail(ticket)}
                        className="p-4 border border-white/10 rounded-lg hover:border-primary/50 cursor-pointer transition-all bg-black/50"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              {getStatusBadge(ticket.status)}
                              {getPriorityBadge(ticket.priority)}
                              <span className="text-xs text-muted-foreground font-mono">
                                {getCategoryLabel(ticket.category)}
                              </span>
                            </div>
                            <h3 className="font-mono font-semibold truncate">{ticket.subject}</h3>
                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                              {ticket.messages?.[0]?.message || ""}
                            </p>
                          </div>
                          <div className="text-right text-xs text-muted-foreground font-mono">
                            <div className="flex items-center gap-1">
                              <MessageCircle className="w-3 h-3" />
                              {ticket.messages?.length || 0}
                            </div>
                            <div className="mt-1">
                              {new Date(ticket.updated_at).toLocaleDateString(language === "tr" ? "tr-TR" : "en-US")}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {totalPages > 1 && (
                    <DataPagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={setCurrentPage}
                    />
                  )}
                </>
              )}
            </div>

            {/* Support Info Sidebar */}
            <div className="space-y-6">
              {/* Telegram Support */}
              {supportInfo?.telegram_usernames?.length > 0 && (
                <div className="p-4 border border-white/10 rounded-lg bg-black/50">
                  <h3 className="font-mono font-bold mb-3 flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-blue-400" />
                    {t("support.telegramSupport")}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">{t("support.telegramDesc")}</p>
                  
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground font-mono mb-2">{t("support.adminTelegram")}:</p>
                    {supportInfo.telegram_usernames.map((username, index) => (
                      <a
                        key={index}
                        href={`https://t.me/${username}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-2 rounded bg-blue-500/10 hover:bg-blue-500/20 transition-colors"
                      >
                        <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
                          <MessageCircle className="w-4 h-4 text-blue-400" />
                        </div>
                        <span className="font-mono text-blue-400">@{username}</span>
                        <ExternalLink className="w-3 h-3 text-blue-400 ml-auto" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Working Hours */}
              {supportInfo && (
                <div className="p-4 border border-white/10 rounded-lg bg-black/50">
                  <h3 className="font-mono font-bold mb-3 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-primary" />
                    {t("support.workingHours")}
                  </h3>
                  <p className="font-mono text-lg">{supportInfo.working_hours || "09:00 - 18:00"}</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    <span className="font-semibold">{t("support.responseTime")}:</span> {supportInfo.response_time || "24 saat içinde"}
                  </p>
                  {supportInfo.support_email && (
                    <p className="text-sm text-muted-foreground mt-2">
                      <span className="font-semibold">Email:</span> {supportInfo.support_email}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </CustomerLayout>

      {/* New Ticket Dialog */}
      <Dialog open={newTicketOpen} onOpenChange={setNewTicketOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-mono">{t("support.newTicket")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-mono text-muted-foreground mb-2 block">{t("support.ticketCategory")}</label>
              <Select value={ticketCategory} onValueChange={setTicketCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">{t("support.categoryGeneral")}</SelectItem>
                  <SelectItem value="technical">{t("support.categoryTechnical")}</SelectItem>
                  <SelectItem value="billing">{t("support.categoryBilling")}</SelectItem>
                  <SelectItem value="other">{t("support.categoryOther")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-mono text-muted-foreground mb-2 block">{t("support.ticketSubject")}</label>
              <Input
                value={ticketSubject}
                onChange={(e) => setTicketSubject(e.target.value)}
                placeholder={language === "tr" ? "Konu başlığı..." : "Subject..."}
                className="font-mono"
              />
            </div>
            <div>
              <label className="text-sm font-mono text-muted-foreground mb-2 block">{t("support.ticketMessage")}</label>
              <Textarea
                value={ticketMessage}
                onChange={(e) => setTicketMessage(e.target.value)}
                placeholder={language === "tr" ? "Mesajınızı yazın..." : "Write your message..."}
                rows={5}
                className="font-mono"
              />
            </div>
            <Button onClick={handleCreateTicket} disabled={submitting} className="w-full font-mono">
              {submitting ? "..." : t("support.sendTicket")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ticket Detail Dialog */}
      <Dialog open={ticketDetailOpen} onOpenChange={setTicketDetailOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-mono flex items-center gap-2">
              {selectedTicket && getStatusBadge(selectedTicket.status)}
              <span className="truncate">{selectedTicket?.subject}</span>
            </DialogTitle>
          </DialogHeader>
          
          {selectedTicket && (
            <div className="py-4">
              <div className="flex items-center gap-4 text-xs text-muted-foreground font-mono mb-4">
                <span>{getCategoryLabel(selectedTicket.category)}</span>
                <span>•</span>
                <span>{new Date(selectedTicket.created_at).toLocaleString(language === "tr" ? "tr-TR" : "en-US")}</span>
              </div>

              {/* Messages */}
              <div className="space-y-4 mb-6 max-h-[40vh] overflow-y-auto">
                {selectedTicket.messages?.map((msg, index) => (
                  <div
                    key={msg.id}
                    className={`p-4 rounded-lg ${
                      msg.sender_role === "user" 
                        ? "bg-primary/10 border border-primary/20 ml-8" 
                        : "bg-white/5 border border-white/10 mr-8"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                        ["admin", "super_admin"].includes(msg.sender_role) ? "bg-blue-500/20" : "bg-primary/20"
                      }`}>
                        <User className="w-3 h-3" />
                      </div>
                      <span className="font-mono text-sm font-semibold">{msg.sender_name || "User"}</span>
                      {["admin", "super_admin"].includes(msg.sender_role) && (
                        <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded font-mono">ADMIN</span>
                      )}
                      <span className="text-xs text-muted-foreground ml-auto">
                        {new Date(msg.created_at).toLocaleString(language === "tr" ? "tr-TR" : "en-US")}
                      </span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                  </div>
                ))}
              </div>

              {/* Reply Form */}
              {selectedTicket.status !== "closed" && (
                <div className="border-t border-white/10 pt-4">
                  <Textarea
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    placeholder={t("support.replyPlaceholder")}
                    rows={3}
                    className="font-mono mb-3"
                  />
                  <Button onClick={handleReply} disabled={sendingReply || !replyMessage.trim()} className="font-mono">
                    <Send className="w-4 h-4 mr-2" />
                    {sendingReply ? "..." : t("support.sendReply")}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SupportPage;
